// The values-at-cursor readout — the numbers that used to ride in a box on top
// of the plot.
//
// It was a canvas tooltip drawn at the pointer, and it had the two faults you
// get for free with that shape. It covered the traces it was describing, so
// reading a value meant hiding the lines either side of it; and its height grew
// with the number of rows, so on a 76 px track carrying four models, a buoy and
// the map field's own trace it was taller than the canvas it lived in. The
// clamp pinned it to y = 2 and the surplus rows were simply drawn past the
// bottom edge and lost — always the LAST rows, which is where the map field's
// trace sits. That is the whole of "when there is observed data in the box, the
// map observation is cut off": not a data failure, a geometry one.
//
// So the text is DOM now and lives above the plot, in the block's own anatomy
// between the header and the canvas. It cannot occlude, because it is not over
// anything; it cannot clip, because it wraps and the block grows. Only the
// crosshair stayed on the canvas, which is right — that is the part that has to
// track the pointer.
//
// The model below is pure and tested. The renderer is a straight diff onto
// nodes we own, in the house style: mono for data, a swatch for identity, no
// colour in the chrome.

import type { Chart } from "./chart.js";

/** One series' line: its swatch colour, its name, its value at the index. */
export interface ReadoutRow {
  label: string;
  color: string;
  value: string;
}

export interface ReadoutModel {
  /** The instant being described, already formatted by the caller's clock. */
  title: string;
  rows: ReadoutRow[];
  /** Muted footer lines (the squall reasoning), verbatim. */
  extras: string[];
  index: number | null;
  /** True when the index came from a pointer or the shared playhead; false when
   *  nothing is being pointed at and we fell back to the present hour. The
   *  difference is worth stating — "12.4 kn" means something else if the reader
   *  thinks it is the hour they are pointing at and it is actually now. */
  live: boolean;
}

/** The shape buildReadout needs from a series. `Series` satisfies it. */
export interface ReadoutSeries {
  label: string;
  color: string;
  data: readonly (number | null)[];
  noTooltip?: boolean;
}

export interface ReadoutInput {
  series: readonly ReadoutSeries[];
  index: number | null;
  live: boolean;
  title: string;
  /** Display unit, already converted upstream ("kn", "m"/"ft", ""). */
  unit: string;
  extras: readonly string[];
}

/**
 * Which sample the readout describes.
 *
 * Four candidates in order of authority: this chart's own pointer, the shared
 * playhead, the present hour, and the last sample the series has. The last two
 * are the reason the row is never blank — a readout that empties itself the
 * moment you move the mouse away teaches you to keep the mouse still, which is
 * exactly the habit the floating box already imposed. The conditions table made
 * the same call: it shows the present hour when nothing is scrubbed.
 */
export function readoutIndex(
  hover: number | null,
  cursor: number | null,
  now: number | null,
  count: number,
): { index: number | null; live: boolean } {
  if (hover !== null) return { index: hover, live: true };
  if (cursor !== null) return { index: cursor, live: true };
  if (now !== null) return { index: now, live: false };
  if (count > 0) return { index: count - 1, live: false };
  return { index: null, live: false };
}

/** Format one value the way the tooltip box did: integers bare, else one decimal. */
export function fmtReadoutValue(v: number | null | undefined, unit: string): string {
  if (v == null || !Number.isFinite(v)) return "—";
  const n = Number.isInteger(v) ? String(v) : v.toFixed(1);
  return unit.length > 0 ? `${n} ${unit}` : n;
}

/** Build the readout for one sample. Nothing is dropped or truncated here; the
 *  renderer wraps whatever this returns. */
export function buildReadout(input: ReadoutInput): ReadoutModel {
  const { index, live, title, unit } = input;
  if (index === null) {
    return { title, rows: [], extras: [], index: null, live };
  }
  const rows: ReadoutRow[] = [];
  for (const s of input.series) {
    // Same exclusions the box used: the transparent envelope edges carry
    // noTooltip, and an unlabelled series has nothing to say in a list.
    if (s.noTooltip === true) continue;
    if (s.label.length === 0) continue;
    rows.push({ label: s.label, color: s.color, value: fmtReadoutValue(s.data[index], unit) });
  }
  return { title, rows, extras: [...input.extras], index, live };
}

/** A stable string for a model, so a redraw that changes nothing writes no DOM. */
export function readoutSignature(m: ReadoutModel): string {
  return [
    m.live ? "1" : "0",
    m.title,
    ...m.rows.map((r) => `${r.color}${r.label}${r.value}`),
    ...m.extras.map((x) => `x${x}`),
  ].join("");
}

// --- renderer ---------------------------------------------------------------

const lastSig = new WeakMap<HTMLElement, string>();

/**
 * The tallest this strip has been since its chart was mounted, held as a
 * min-height.
 *
 * Without it the strip breathes: the squall reasoning is one more line, so
 * sweeping across a flagged hour grows the strip and shoves the plot down
 * under the pointer, then pulls it back up on the way out. The playhead is
 * still on the right hour, but the traces jump, and a chart that twitches
 * while you read it is its own kind of unreadable.
 *
 * Monotone on purpose: it only ever grows, so the jump happens at most once
 * per chart and never again — and it is a MINIMUM, not a height, so a taller
 * model still grows the strip rather than being cut off. Cleared when a chart
 * is remounted, because a new point is a new set of rows.
 */
const highWater = new WeakMap<HTMLElement, number>();

function reserve(host: HTMLElement): void {
  const h = host.offsetHeight; // one layout read, and only when the text changed
  if (h > (highWater.get(host) ?? 0)) {
    highWater.set(host, h);
    host.style.minHeight = `${h}px`;
  }
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

/** Paint a model into a `.creadout` element. Idempotent; cheap when unchanged. */
export function renderReadout(host: HTMLElement, m: ReadoutModel): void {
  const sig = readoutSignature(m);
  if (lastSig.get(host) === sig) return;
  lastSig.set(host, sig);

  if (m.index === null) {
    host.replaceChildren();
    host.hidden = true;
    return;
  }
  host.hidden = false;

  const kids: HTMLElement[] = [];
  // The time first, and when we are showing the present hour rather than a
  // pointed-at one, say so in the same breath.
  const t = el("span", "cr-time", m.live ? m.title : `now · ${m.title}`);
  if (!m.live) t.classList.add("idle");
  kids.push(t);

  for (const r of m.rows) {
    const row = el("span", "cr-row");
    const sw = el("i", "cr-sw");
    sw.style.background = r.color;
    row.append(sw, el("span", "cr-lab", r.label), el("span", "cr-val", r.value));
    kids.push(row);
  }
  for (const x of m.extras) kids.push(el("span", "cr-extra", x));

  host.replaceChildren(...kids);
  reserve(host);
}

/** Wire a chart's readout to a host element. Returns the unsubscribe. */
export function attachReadout(chart: Chart, host: HTMLElement): () => void {
  // A remount is new data: forget what the last point's rows measured.
  highWater.delete(host);
  lastSig.delete(host);
  host.style.minHeight = "";
  return chart.subscribeReadout((m) => renderReadout(host, m));
}
